import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface FAQ {
  question: string;
  answer: string | string[];
}

const SupportPage: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expanded, setExpanded] = useState<string | false>(false);

  // Load FAQ data on mount
  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await fetch('/faq.json');
        if (!response.ok) {
          throw new Error('Failed to load FAQ data');
        }
        const data = await response.json();
        setFaqs(data);
        setFilteredFaqs(data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading FAQs:', err);
        setError('Unable to load FAQ data. Please try again later.');
        setLoading(false);
      }
    };

    loadFaqs();
  }, []);

  // Filter FAQs based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFaqs(faqs);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = faqs.filter((faq) => {
      const answerText = Array.isArray(faq.answer) 
        ? faq.answer.join(' ').toLowerCase() 
        : faq.answer.toLowerCase();
      return (
        faq.question.toLowerCase().includes(query) ||
        answerText.includes(query)
      );
    });
    setFilteredFaqs(filtered);
  }, [searchQuery, faqs]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Support Center
          </Typography>
        </Box>

        {/* Contact Information Section */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">
            If you are unable to find the answers to the questions you are looking for in the articles below, 
            please reach out to us at <strong><a href="mailto:support@ateevo.com" style={{ color: 'inherit' }}>support@ateevo.com</a></strong>. 
            We will respond to you via email within 24 business hours.
          </Typography>
        </Alert>

        {/* Search Box */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search for help..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        </Box>

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* FAQ List */}
        {!loading && !error && (
          <>
            {filteredFaqs.length === 0 ? (
              <Alert severity="warning">
                No articles found matching your search. Please try different keywords or contact us directly.
              </Alert>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Frequently Asked Questions
                </Typography>
                {filteredFaqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel${index}`}
                    onChange={handleAccordionChange(`panel${index}`)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                      <Typography variant="subtitle1" fontWeight="medium">
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {Array.isArray(faq.answer) ? (
                        faq.answer.map((paragraph, pIndex) => (
                          <Typography 
                            key={pIndex} 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ mb: pIndex < faq.answer.length - 1 ? 2 : 0 }}
                          >
                            {paragraph}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {faq.answer}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SupportPage;
